import _ from 'lodash';
import { CreditCardData } from '../__autogenerated__/cards/CreditCardData';
import { CreditCard } from '../__autogenerated__/types/CreditCard';
import { CreditCardRewardsType } from '../__autogenerated__/types/CreditCardRewardsTypes';
import {
  EarnRateInfo,
  ExtendedEarnRateInfo,
} from '../../types/CardRewardsEarnRates';

import { CategoryType } from '../__autogenerated__';
import { useMemo } from 'react';
import {
  AdvancedCategory,
  CardRewardsEarnRate,
  isQuarterlyCategoriesEarnRate,
  isSelectableCategoriesEarnRate,
  isAdvancedCategory,
  isFixedCategoryGroupEarnRate,
  SelectableCategoriesEarnRate,
} from '../../types';

// Type for supporting multiple selectable category rates per card
// Supports both legacy Record-keyed-by-card-type and new index-based array format
export type SelectedCategoriesMultiRate =
  | Partial<Record<CreditCard, string[][][]>>
  | (string[][][] | undefined)[];

export const getSelectedCategories = ({
  userSelectedCategories = [],
  reward,
}: {
  userSelectedCategories?: string[][];
  reward: SelectableCategoriesEarnRate;
}) => {
  // are we just overcomplicating things...?
  // This is the number of categories that are not part of the user's selection
  const numCategoriesToFill =
    (reward?.selectableCategories?.applicableCategoryCount || 0) -
    (userSelectedCategories?.length || 0);

  const allRewardCategories = reward.selectableCategories.categories.map(
    (c) => {
      if (isAdvancedCategory(c)) {
        return c.categories;
      }
      return [c];
    }
  );

  const selectedCategories = [
    ...userSelectedCategories,
    ..._.differenceWith(
      allRewardCategories,
      userSelectedCategories,
      _.isEqual
    ).slice(0, numCategoriesToFill),
  ];
  const applicableCategories = selectedCategories.flatMap(
    (c: string | AdvancedCategory | string[]) => {
      if (Array.isArray(c)) {
        return c;
      }
      if (isAdvancedCategory(c)) {
        return c.categories;
      }
      return [c];
    }
  );

  return {
    applicableCategories,
    selectedCategories,
    allRewardCategories,
  };
};

// Enhanced version that works with multi-rate structure
export const getSelectedCategoriesForRate = ({
  userSelectedCategoriesForCard = [],
  reward,
  rateIndex,
}: {
  userSelectedCategoriesForCard?: string[][][];
  reward: SelectableCategoriesEarnRate;
  rateIndex: number;
}) => {
  // Get selections for this specific rate index
  const userSelectedCategories = userSelectedCategoriesForCard[rateIndex] || [];

  // Use the existing logic for this specific rate
  return getSelectedCategories({
    userSelectedCategories,
    reward,
  });
};

export const getCurrentQuarter = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentQuarter = Math.floor(currentMonth / 3) + 1;
  return `Q${currentQuarter}`;
};

/**
 * Calculates a tie-breaking score for cards with identical total values.
 * Higher scores indicate better cards for tie-breaking purposes.
 */
const calculateTieBreakingScore = (
  cardItem: ExtendedEarnRateInfo,
  allCategoryCards: ExtendedEarnRateInfo[],
  allCards: CreditCard[]
): number => {
  const cardData = CreditCardData[cardItem.card];
  if (!cardData) return 0;

  let score = 0;

  // 1. Card Consistency: Prefer cards already used for other categories
  const otherCategoriesUsingThisCard = allCategoryCards.filter(
    (item) => item.card === cardItem.card && item.category !== cardItem.category
  ).length;
  score += otherCategoriesUsingThisCard * 10;

  // 2. Spend Cap: Prefer cards with higher/no spend caps
  const earnRateForCategory = cardData.rewards_earn_rates?.find((rate) => {
    if (isSelectableCategoriesEarnRate(rate)) {
      return rate.selectableCategories.categories.some((c) =>
        isApplicableCategory(cardItem.category, c)
      );
    }
    return isApplicableCategory(cardItem.category, rate.category);
  });

  if (earnRateForCategory) {
    if (!earnRateForCategory.spendCap) {
      score += 5; // No cap is best
    } else if (earnRateForCategory.spendCap > 5000) {
      score += 3; // High cap is good
    } else if (earnRateForCategory.spendCap > 1000) {
      score += 1; // Medium cap is okay
    }
  }

  // 3. Activation Requirements: Prefer cards that don't require user selection
  if (
    earnRateForCategory &&
    isSelectableCategoriesEarnRate(earnRateForCategory)
  ) {
    if (!earnRateForCategory.selectableCategories.requireUserSelection) {
      score += 3;
    }
  }

  // 4. Travel Portal Requirements: Prefer cards that don't require travel portal
  if (earnRateForCategory && !earnRateForCategory.requiresTravelPortal) {
    score += 2;
  }

  // 5. Foreign Transaction Fees: Prefer cards with lower/no foreign transaction fees
  const fxFee = cardData.foreignTransactionFee || 0;
  if (fxFee === 0) {
    score += 2;
  } else if (fxFee < 0.02) {
    score += 1;
  }

  // 6. Annual Fees: Prefer cards with lower annual fees
  const annualFee = cardData.annualFee || 0;
  if (annualFee === 0) {
    score += 2;
  } else if (annualFee < 100) {
    score += 1;
  }

  // 7. APR: Prefer cards with lower APR
  const apr = cardData.apr?.low || 0;
  if (apr > 0 && apr < 0.2) {
    score += 1;
  }

  return score;
};

// Categories that don't inherit from allPurchases (e.g., rent/housing require special platforms like Bilt)
const standaloneCategories = ['rent', 'housing'];

const isApplicableCategory = (
  category: string,
  cardCategory: string | AdvancedCategory
) => {
  if (typeof cardCategory === 'string') {
    // allPurchases applies to everything except standalone categories
    if (cardCategory === 'allPurchases') {
      return !standaloneCategories.includes(category);
    }
    return (
      cardCategory === category ||
      category.startsWith(cardCategory + '.')
    );
  }

  return cardCategory.categories.some((c) =>
    isApplicableCategoryString(category, c)
  );
};

const isApplicableCategoryString = (category: string, cardCategory: string) => {
  // allPurchases applies to everything except standalone categories
  if (cardCategory === 'allPurchases') {
    return !standaloneCategories.includes(category);
  }
  return (
    cardCategory === category ||
    category.startsWith(cardCategory + '.')
  );
};

/**
 * For each category, this returns a list of the best earn rates and credits for that category.
 *
 * @param cards
 * @param categories
 * @param pointValues
 * @param enableForeignTransactionFees
 */
// Legacy version - maintains backward compatibility
export const useCategoryEarnRatesAndCredits = (
  cards: CreditCard[],
  categories: CategoryType[],
  pointValues: Record<string, number>,
  isPurchasingInUSA: boolean,
  selectedCategoriesByCard?: Partial<Record<CreditCard, string[][]>>
) => {
  // Convert legacy format to new format for internal processing
  const convertedCategories: SelectedCategoriesMultiRate = {};
  if (selectedCategoriesByCard) {
    Object.entries(selectedCategoriesByCard).forEach(([card, selections]) => {
      if (selections) {
        // Wrap single selection set in array to represent first rate
        convertedCategories[card as CreditCard] = [selections];
      }
    });
  }

  return useCategoryEarnRatesAndCreditsV2(
    cards,
    categories,
    pointValues,
    isPurchasingInUSA,
    convertedCategories,
    undefined // No promotional types filtering for legacy hook
  );
};

// New version that supports multiple rates per card
export const useCategoryEarnRatesAndCreditsV2 = (
  cards: CreditCard[],
  categories: CategoryType[],
  pointValues: Record<string, number>,
  isPurchasingInUSA: boolean,
  selectedCategoriesByCard?: SelectedCategoriesMultiRate,
  promotionalTypes?: string[]
) => {
  /**
   * Returns in a format of:
   *
   * {
   *   [categoryType]: {
   *     cardsWithCredits: EarnRateInfo[],
   *     sortedCardsByEarnRate: EarnRateInfo[],
   *   }
   * }
   */
  // Memoize promotional types array
  const activePromotionalTypes = useMemo(() => {
    return promotionalTypes || [];
  }, [promotionalTypes]);

  const result = useMemo(() => {
    const result: Record<
      CategoryType,
      {
        cardsWithCredits: EarnRateInfo[];
        sortedCardsByEarnRate: EarnRateInfo[];
      }
    > = {} as Record<
      CategoryType,
      {
        cardsWithCredits: EarnRateInfo[];
        sortedCardsByEarnRate: EarnRateInfo[];
      }
    >;

    // Process each category
    categories.forEach((category) => {
      const categoryCards: Array<ExtendedEarnRateInfo> = [];

      // When outside USA, foreign transaction rates can apply to any category
      // since all purchases abroad are foreign transactions
      const shouldCheckForeignTransactions = !isPurchasingInUSA;

      // Process each card for this category
      cards.forEach((cardId, cardIndex) => {
        const cardData = CreditCardData[cardId];
        if (!cardData) return;

        // Apply foreign transaction fee penalty instead of filtering out cards
        let fxPenalty = 0;
        if (!isPurchasingInUSA && (cardData as any).foreignTransactionFee > 0) {
          fxPenalty = (cardData as any).foreignTransactionFee;
        }

        // Find the earn rate for this category
        let earnRate = 0;
        let hasCredits = false;
        let promotionalType: string | null = null;

        // Check if the card has rewards for this category
        if (
          cardData.rewards_earn_rates &&
          Array.isArray(cardData.rewards_earn_rates)
        ) {
          // Get selectable rates for this card to handle multiple rates properly
          const selectableRates = cardData.rewards_earn_rates.filter((rate) =>
            isSelectableCategoriesEarnRate(rate)
          ) as SelectableCategoriesEarnRate[];

          cardData.rewards_earn_rates.forEach(
            (reward: CardRewardsEarnRate, rewardIndex) => {
              if (reward.requiresTravelPortal) {
                // Skip rewards that require the travel portal
                return;
              }

              let rewardApplies = false;

              if (isSelectableCategoriesEarnRate(reward)) {
                // Find the index of this rate among selectable rates
                const rateIndex = selectableRates.findIndex(
                  (rate) => rate === reward
                );
                if (rateIndex >= 0) {
                  const cardCategories = Array.isArray(selectedCategoriesByCard)
                    ? selectedCategoriesByCard[cardIndex]
                    : selectedCategoriesByCard?.[cardId];
                  const { applicableCategories } = getSelectedCategoriesForRate(
                    {
                      userSelectedCategoriesForCard:
                        cardCategories || [],
                      reward,
                      rateIndex,
                    }
                  );
                  if (
                    applicableCategories.some((c) =>
                      isApplicableCategoryString(category, c)
                    )
                  ) {
                    rewardApplies = true;
                  }
                }
              }

              if (!rewardApplies && isFixedCategoryGroupEarnRate(reward)) {
                // Check if this fixed category group is currently active (date filtering)
                const now = new Date();
                const startDate = reward.selectableCategories?.startDate
                  ? new Date(reward.selectableCategories.startDate)
                  : null;
                const endDate = reward.selectableCategories?.endDate
                  ? new Date(reward.selectableCategories.endDate)
                  : null;

                const isCurrentlyActive =
                  (!startDate || now >= startDate) &&
                  (!endDate || now <= endDate);

                if (
                  isCurrentlyActive &&
                  reward.selectableCategories.categories.some((c) =>
                    isApplicableCategory(category, c)
                  )
                ) {
                  rewardApplies = true;
                }
              }

              if (!rewardApplies && isQuarterlyCategoriesEarnRate(reward)) {
                const currentQuarter = getCurrentQuarter();
                const categoryString = category.toString();
                if (
                  reward.selectableCategories.categories.find(
                    (c: AdvancedCategory | string) =>
                      isAdvancedCategory(c)
                        ? c.active_quarter === currentQuarter &&
                          c.categories.find((innerCategory) =>
                            isApplicableCategory(categoryString, innerCategory)
                          )
                        : isApplicableCategory(categoryString, c)
                  )
                ) {
                  rewardApplies = true;
                }
              }

              // Check for foreignTransactions earn rate when outside USA
              if (
                shouldCheckForeignTransactions &&
                reward.category === 'foreignTransactions'
              ) {
                rewardApplies = true;
              }

              if (
                !rewardApplies &&
                isApplicableCategory(category, reward.category)
              ) {
                rewardApplies = true;
              }

              if (rewardApplies) {
                // Start with base earn rate - explicitly exclude signupBonusEarnRateIncreasePercentage
                // (signup bonus earn rate increases are not included in best earn rate calculations)
                let rewardBestRate = reward.earnRate;
                let rewardPromotionalType: string | null = null;

                // Filter promotions based on selected promotional types
                if (Array.isArray(reward.promotions)) {
                  if (activePromotionalTypes.length > 0) {
                    // User has selected promotional types - only use matching promotions
                    const matchingPromotions = reward.promotions.filter(
                      (promotion) => {
                        if (
                          !promotion ||
                          typeof promotion.earnRate !== 'number' ||
                          promotion.isSignupBonusEarnRate ||
                          promotion.signupBonusDurationMonths != null
                        ) {
                          return false;
                        }

                        // If promotion has a promotional_type, check if it matches any selected type
                        if (promotion.promotional_type) {
                          // Convert both to strings for comparison (handles enum types)
                          const promoTypeStr = String(
                            promotion.promotional_type
                          );
                          return activePromotionalTypes.some(
                            (selectedType: string) =>
                              String(selectedType) === promoTypeStr
                          );
                        }

                        // If no promotional_type is set, exclude it when promotional types are selected
                        return false;
                      }
                    );

                    // Find the promotion with the maximum earn rate and track its type
                    if (matchingPromotions.length > 0) {
                      let bestPromoRate = rewardBestRate;
                      for (const promo of matchingPromotions) {
                        if (promo.earnRate > bestPromoRate) {
                          bestPromoRate = promo.earnRate;
                          rewardPromotionalType = promo.promotional_type
                            ? String(promo.promotional_type)
                            : null;
                        }
                      }
                      rewardBestRate = bestPromoRate;
                    }
                    // If no matching promotions, keep base rate (already set above)
                  } else {
                    // No promotional types selected - use base rate only
                    // (don't include any promotional earn rates)
                    rewardBestRate = reward.earnRate;
                  }
                }

                if (rewardBestRate > earnRate) {
                  earnRate = rewardBestRate;
                  promotionalType = rewardPromotionalType;
                }
              }
            }
          );

          // Check if this card has credits (annual credits, statement credits, etc.)
          if (cardData.benefits) {
          }
        }

        // Add to the array of cards for this category - we'll sort by this later
        if (earnRate > 0) {
          const pointValue = pointValues[cardData.rewardsType] || 1;
          // Apply FX penalty by reducing the effective earn rate
          const effectiveEarnRate = earnRate - fxPenalty * 100;
          const totalValue = pointValue * effectiveEarnRate;

          categoryCards.push({
            key: `${cardId}_${cardIndex}_${category}`,
            earnRate,
            card: cardId,
            cardIndex,
            rewardsType: cardData.rewardsType,
            spend: 0, // TODO: Add monthly spend calculation if needed
            totalValue,
            effectiveEarnRate,
            category,
            hasCredits,
            fxPenalty,
            pointValue,
            promotionalType,
          });
        }
      });

      // Sort cards by total value (highest first) with tie-breaking logic
      const sortedCardsByEarnRate: EarnRateInfo[] = categoryCards
        .sort((a, b) => {
          // Primary sort: total value (highest first)
          if (b.totalValue !== a.totalValue) {
            return b.totalValue - a.totalValue;
          }

          // Tie-breaking logic for cards with identical total values
          return (
            calculateTieBreakingScore(b, categoryCards, cards) -
            calculateTieBreakingScore(a, categoryCards, cards)
          );
        })
        .map((item) => ({
          key: item.key,
          earnRate: item.earnRate,
          card: item.card,
          cardIndex: item.cardIndex,
          category: item.category,
          rewardsType: item.rewardsType,
          spend: item.spend,
          totalValue: item.totalValue,
          promotionalType: item.promotionalType,
        }));

      // Get cards with credits
      const cardsWithCredits: EarnRateInfo[] = categoryCards
        .filter((item) => item.hasCredits)
        .sort((a, b) => {
          // Primary sort: total value (highest first)
          if (b.totalValue !== a.totalValue) {
            return b.totalValue - a.totalValue;
          }

          // Tie-breaking logic for cards with identical total values
          return (
            calculateTieBreakingScore(b, categoryCards, cards) -
            calculateTieBreakingScore(a, categoryCards, cards)
          );
        })
        .map((item) => ({
          key: item.key,
          category: item.category,
          earnRate: item.earnRate,
          card: item.card,
          cardIndex: item.cardIndex,
          rewardsType: item.rewardsType,
          spend: item.spend,
          totalValue: item.totalValue,
          promotionalType: item.promotionalType,
        }));

      result[category] = {
        cardsWithCredits,
        sortedCardsByEarnRate,
      };
    });

    return result;
  }, [
    cards,
    categories,
    pointValues,
    isPurchasingInUSA,
    selectedCategoriesByCard,
    activePromotionalTypes,
  ]);

  return result;
};
